// =============================================================================
// ppiUI — Design tokens + componentes compartilhados do submódulo PPI
// =============================================================================
// Clone do psUI (Processo Seletivo), na COR DO TREINAMENTO (#0284c7) — o PPI
// migrou do RH para o módulo Treinamento (2026-07-15) e adota o azul dele. Mesma
// regra: acento único no "cromo" (header de tabela, abas, botões primários) e
// SEMÂNTICA DE STATUS separada (aprovado=verde etc.).
// Estilo do projeto: inline styles em JS (NÃO Tailwind).

import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Cor de acento do módulo (Treinamento) + neutros ─────────────────────────
export const CORES = {
    primaria: '#0284c7',
    primariaEscura: '#0369a1',
    primariaBg: '#e0f2fe',
    gradiente: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',

    texto: '#0f172a',
    textoSec: '#475569',
    textoFraco: '#64748b',
    borda: '#e2e8f0',
    bgPagina: '#f8fafc',
    bgSuave: '#f8fafc',
    branco: '#fff',
};

// ─── Semântica de status (badges) — NÃO usar a cor de acento aqui ─────────────
export const STATUS = {
    aprovado:   { bg: '#ecfdf5', fg: '#047857' }, // verde
    emProcesso: { bg: '#eff6ff', fg: '#1d4ed8' }, // azul
    inscrito:   { bg: '#f5f3ff', fg: '#6d28d9' }, // roxo
    reprovado:  { bg: '#fff1f2', fg: '#be123c' }, // vermelho
    rascunho:   { bg: '#f1f5f9', fg: '#475569' }, // cinza
    ativo:      { bg: '#ecfdf5', fg: '#047857' }, // verde (inscrições abertas)
    // Âmbar = "precisa de você, mas não é falha do sistema". Usado no "Não enviado"
    // do card de Envios: o número está errado no CADASTRO, e quem resolve é o RH —
    // é uma situação diferente de "o ManyChat recusou" (vermelho), e misturar as
    // duas na mesma cor esconderia que a ação a tomar é outra.
    alerta:     { bg: '#fffbeb', fg: '#b45309' }, // âmbar
    neutro:     { bg: '#f1f5f9', fg: '#475569' },
};

// ─── Badge de status (pílula) ────────────────────────────────────────────────
export function BadgeStatus({ tom = 'neutro', cor, children, icone: Icone }) {
    const c = cor || STATUS[tom] || STATUS.neutro;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '999px', fontSize: '11px',
            fontWeight: 800, whiteSpace: 'nowrap', backgroundColor: c.bg, color: c.fg,
        }}>
            {Icone && <Icone size={12} />}
            {children}
        </span>
    );
}

// ─── Botão padronizado ───────────────────────────────────────────────────────
const VARIANTES_BOTAO = {
    primario:   { backgroundColor: CORES.primaria, color: '#fff', border: 'none' },
    secundario: { backgroundColor: '#fff', color: CORES.textoSec, border: `1px solid ${CORES.borda}` },
    perigo:     { backgroundColor: '#fff', color: '#b91c1c', border: '1px solid #fecaca' },
    sucesso:    { backgroundColor: '#059669', color: '#fff', border: 'none' },
    neutro:     { backgroundColor: '#0f172a', color: '#fff', border: 'none' },
};
export function Botao({ variant = 'primario', icone: Icone, children, style, disabled, ...rest }) {
    const v = VARIANTES_BOTAO[variant] || VARIANTES_BOTAO.primario;
    return (
        <button
            disabled={disabled}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s', ...v, ...style,
            }}
            {...rest}
        >
            {Icone && <Icone size={16} />}
            {children}
        </button>
    );
}

// ─── Cartão de KPI (número grande) ───────────────────────────────────────────
export function CartaoKpi({ titulo, valor, cor = CORES.primaria, icone: Icone, sufixo, badge }) {
    return (
        <div style={{
            backgroundColor: '#fff', padding: '20px', borderRadius: '14px',
            border: `1px solid ${CORES.borda}`, borderLeft: `4px solid ${cor}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
            <div>
                <h4 style={{ color: CORES.textoFraco, fontSize: '12px', textTransform: 'uppercase', margin: '0 0 8px 0', fontWeight: 800, letterSpacing: '0.3px' }}>{titulo}</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '28px', fontWeight: 900, color: CORES.texto, lineHeight: 1 }}>{valor}{sufixo}</span>
                    {badge && <span style={{ fontSize: '11px', fontWeight: 800, color: cor, backgroundColor: hexParaBg(cor), padding: '3px 8px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{badge}</span>}
                </div>
            </div>
            {Icone && (
                <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: hexParaBg(cor), color: cor, flexShrink: 0 }}>
                    <Icone size={24} />
                </div>
            )}
        </div>
    );
}

// ─── Seletor múltiplo (recorte do público) ───────────────────────────────────
// Caixa com busca + checkboxes. **Nada selecionado = TODOS** (é a regra do
// backend: dimensão vazia não filtra) — por isso o rodapé mostra "Todos" quando
// está vazio, para o RH não achar que esqueceu de escolher.
export function SeletorMulti({ label, opcoes = [], selecionados = [], onChange, dica }) {
    const [busca, setBusca] = useState('');
    const filtradas = opcoes.filter((o) => String(o).toLowerCase().includes(busca.toLowerCase()));
    const alternar = (v) =>
        onChange(selecionados.includes(v) ? selecionados.filter((x) => x !== v) : [...selecionados, v]);

    return (
        <div>
            <label style={estilos.rotuloFiltro}>{label}</label>
            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: '#fff', overflow: 'hidden' }}>
                <input
                    placeholder={dica || 'Buscar…'}
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: 'none', borderBottom: `1px solid ${CORES.borda}`, boxSizing: 'border-box', fontSize: '13px', outline: 'none' }}
                />
                <div style={{ maxHeight: '150px', overflowY: 'auto', padding: '6px 10px' }}>
                    {!filtradas.length && (
                        <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '6px 0' }}>Nada encontrado.</p>
                    )}
                    {filtradas.map((o) => (
                        <label key={o} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', fontSize: '13px', cursor: 'pointer', color: CORES.texto }}>
                            <input type="checkbox" checked={selecionados.includes(o)} onChange={() => alternar(o)} />
                            <span>{o}</span>
                        </label>
                    ))}
                </div>
                <div style={{ borderTop: `1px solid ${CORES.borda}`, padding: '6px 10px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: CORES.bgSuave }}>
                    <span style={{ fontWeight: 700, color: selecionados.length ? CORES.primaria : CORES.textoFraco }}>
                        {selecionados.length ? `${selecionados.length} selecionado(s)` : 'Todos'}
                    </span>
                    {selecionados.length > 0 && (
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            style={{ background: 'none', border: 'none', color: CORES.textoFraco, cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0 }}
                        >
                            limpar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Paginação de tabela (hook + rodapé) ─────────────────────────────────────
// As tabelas do PPI são <table> com estilo inline, sem componente compartilhado —
// então o que se repetiria em cada tela (fatiar a lista e desenhar o rodapé) mora
// aqui. Regra que o hook resolve sozinho: quando a lista ENCOLHE (filtro novo), a
// página atual pode não existir mais — daí `paginaSegura`, que nunca deixa a tela
// mostrar um vazio "fantasma". Cada tela ainda deve chamar `setPagina(1)` ao
// trocar de filtro, para o usuário não continuar na página 3 de outro recorte.
export function usePaginacao(dados, porPagina = 10) {
    const [pagina, setPagina] = useState(1);

    const totalPaginas = Math.max(1, Math.ceil(dados.length / porPagina));
    const paginaSegura = Math.min(pagina, totalPaginas);
    const inicio = (paginaSegura - 1) * porPagina;
    const paginados = useMemo(
        () => dados.slice(inicio, inicio + porPagina), [dados, inicio, porPagina]);

    return { paginados, pagina: paginaSegura, setPagina, totalPaginas, inicio, total: dados.length };
}

// Rodapé do cartão da tabela: quantos estão à vista + navegação.
// `rotulo` é o nome do que está sendo listado ("candidato(s)", "inscrito(s)"…).
export function RodapePaginacao({ pagina, totalPaginas, inicio, quantidade, total, onPagina, rotulo = 'registro(s)' }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '12px', flexWrap: 'wrap', padding: '10px 16px',
            borderTop: `1px solid ${CORES.borda}`, backgroundColor: CORES.bgSuave,
        }}>
            <span style={{ fontSize: '12px', color: CORES.textoFraco, fontVariantNumeric: 'tabular-nums' }}>
                {inicio + 1}–{inicio + quantidade} de {total} {rotulo}
            </span>
            {totalPaginas > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <BotaoPag onClick={() => onPagina(1)} desabilitado={pagina === 1}>«</BotaoPag>
                    <BotaoPag onClick={() => onPagina(pagina - 1)} desabilitado={pagina === 1}>
                        <ChevronLeft size={14} /> Anterior
                    </BotaoPag>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: CORES.textoSec, padding: '0 6px', fontVariantNumeric: 'tabular-nums' }}>
                        {pagina} / {totalPaginas}
                    </span>
                    <BotaoPag onClick={() => onPagina(pagina + 1)} desabilitado={pagina === totalPaginas}>
                        Próxima <ChevronRight size={14} />
                    </BotaoPag>
                    <BotaoPag onClick={() => onPagina(totalPaginas)} desabilitado={pagina === totalPaginas}>»</BotaoPag>
                </div>
            )}
        </div>
    );
}

const BotaoPag = ({ onClick, desabilitado, children }) => (
    <button onClick={onClick} disabled={desabilitado}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
            border: `1px solid ${CORES.borda}`, backgroundColor: '#fff',
            color: desabilitado ? '#cbd5e1' : CORES.textoSec,
            cursor: desabilitado ? 'default' : 'pointer',
        }}>
        {children}
    </button>
);

// ─── Estilos prontos (objetos reusáveis) ─────────────────────────────────────
export const estilos = {
    tituloSecao: { fontSize: '22px', fontWeight: 800, color: CORES.texto, margin: 0 },
    subtituloSecao: { fontSize: '14px', color: CORES.textoFraco, margin: '4px 0 0 0' },

    input: {
        width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid #cbd5e1`,
        backgroundColor: '#fff', color: CORES.texto, fontSize: '14px', boxSizing: 'border-box',
    },
    rotuloFiltro: { display: 'block', fontSize: '12px', fontWeight: 700, color: CORES.textoSec, marginBottom: '6px' },

    barraFiltros: {
        backgroundColor: '#fff', padding: '16px', borderRadius: '12px',
        border: `1px solid ${CORES.borda}`, marginBottom: '20px',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', alignItems: 'end',
    },

    tabelaWrap: { backgroundColor: '#fff', borderRadius: '12px', border: `1px solid ${CORES.borda}`, overflow: 'hidden' },
    tabela: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: {
        padding: '12px 16px', textAlign: 'left', fontWeight: 800, fontSize: '11px',
        textTransform: 'uppercase', letterSpacing: '0.3px', color: CORES.textoSec,
        backgroundColor: CORES.bgSuave, borderBottom: `2px solid ${CORES.primaria}`, whiteSpace: 'nowrap',
    },
    td: { padding: '12px 16px', color: CORES.texto, borderBottom: `1px solid #f1f5f9`, verticalAlign: 'middle' },
};

// Deriva um fundo claro a partir de uma cor de acento (para o "chip" do ícone do KPI).
function hexParaBg(hex) {
    const mapa = {
        '#0284c7': '#e0f2fe', '#d81d1d': '#fedbdb', '#e11d48': '#ffe4e6', '#3b82f6': '#eff6ff', '#10b981': '#ecfdf5',
        '#f59e0b': '#fffbeb', '#6366f1': '#e0e7ff', '#8b5cf6': '#f5f3ff',
        '#0ea5e9': '#e0f2fe', '#be123c': '#fff1f2', '#059669': '#ecfdf5', '#047857': '#ecfdf5',
    };
    return mapa[hex] || CORES.primariaBg;
}
